import React, { useCallback, useMemo } from "react";
import cn from "clsx";
import useT from "../../hooks/useT";
import { ReactSelectOptionObject } from "../InputSelectCustom";
import "./InputRadioGroup.css";

export interface InputRadioGroupProps {
  /** The ID of the input element. */
  id: string;
  /** The name of the input element. */
  name?: string;
  /** The current value of the input. */
  value: any;
  /** The options to render within the select input. */
  options: ReactSelectOptionObject[];
  /** Sort the options alphabetically. */
  sortOptionsAlphabetically?: boolean;
  /** Placeholder label. */
  placeholder?: string;
  /** Allow the user to easily clear the value. */
  clearable?: boolean;
  /** Mark if the input is disabled. */
  disabled?: boolean;
  /** Extra classes to assign to the input. */
  className?: string;
  /** The language used for translated labels (will default to i18next language if not set). */
  lang?: string;
  /** Event handler for when input is focused. */
  onFocus?: React.ReactEventHandler;
  /** Event handler for when input is blurred. */
  onBlur?: React.ReactEventHandler;
  /** Event handler for when input is changed. */
  onChange?: React.ReactEventHandler;
  /** Event handler for when input is cleared. */
  onClear?: React.ReactEventHandler;
  /** Ref to set the input element to. */
  ref?: React.Ref<HTMLSelectElement>;
}

export const InputRadioGroup = React.forwardRef(
  (
    {
      id,
      name,
      value,
      options,
      clearable = false,
      disabled = false,
      lang,
      className,
      onFocus,
      onBlur,
      onChange,
      onClear
    }: InputRadioGroupProps,
    ref?: React.Ref<HTMLSelectElement>
  ) => {
    const { t, i18n } = useT();
    const lng = lang || i18n.language;

    // Memoized values
    const selectOptions = useMemo(() => {
      return options.map(option => ({
        label: t(option.label, { lng }),
        value: option.value,
        id: id + "_" + option.value
      }));
    }, [options]);

    // Event handlers
    const handleFocus: React.FocusEventHandler = useCallback(
      (event: React.FocusEvent) => {
        if (!!onFocus) {
          onFocus(event);
        }
      },
      [onFocus]
    );
    const handleBlur: React.FocusEventHandler = useCallback(
      (event: React.FocusEvent) => {
        if (!!onBlur) {
          onBlur(event);
        }
      },
      [onBlur]
    );
    const handleChange: React.ChangeEventHandler = useCallback(
      (event: React.ChangeEvent) => {
        if (!!onChange) {
          onChange(event);
        }
      },
      [onChange]
    );

    return (
      <div
        className={cn(
          "input input-select",
          className,
          !!value && "has-value",
          !!clearable && "is-clearable"
        )}
      >
        <div className="input-control input-select-control">
          {selectOptions.map((optionProps, index) => (
            <div key={index} className="option-wrap">
              <input
                type="radio"
                name={name}
                id={optionProps.id}
                value={optionProps.value || ""}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
                checked={optionProps.value === value}
              />
              <label htmlFor={optionProps.id}>{optionProps.label}</label>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

InputRadioGroup.displayName = "InputRadioGroup";

export default InputRadioGroup;
